import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchKpiCalculationData,
  fetchLaunchKpiCalculationData,
  InputAdditionKpiResponse,
  InputKpiResponse,
} from './inputKpiAPI';
import { RootState } from '../../state/store';
import { hideLoader } from '../ui/loaderSlice';

export interface InputKpiState {
  current_price: number;
  competition_price: number;
  current_distribution: number;
  competition_distribution: number;
  ko_segment_ppl: number;
  ko_segment_distribution: number;
  comp_segment_ppl: number;
  comp_segment_distribution: number;
  distribution: string[];
  current_tab: string;
  loading: boolean;
  min_price: number;
  max_price: number;
  error: string | null;
  input_base_price : number
}

const initialState: InputKpiState = {
  current_price: 0,
  competition_price: 0,
  current_distribution: 0,
  competition_distribution: 0,
  ko_segment_ppl: 0,
  ko_segment_distribution: 0,
  comp_segment_ppl: 0,
  comp_segment_distribution: 0,
  distribution: [],
  min_price: 0,
  max_price: 0,
  current_tab: 'Pricing & Distribution',
  loading: false,
  error: null,
  input_base_price : 0
};

export const fetchKpiCalculation = createAsyncThunk(
  'inputKpi/fetchKpiCalculation',
  async (payload: any, { dispatch, rejectWithValue }) => {
    try {
      const response: InputKpiResponse = await fetchKpiCalculationData(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch KPI calculation');
    } finally {
      dispatch(hideLoader());
    }
  },
);

export const fetchLaunchKpiCalculation = createAsyncThunk(
  'inputKpi/fetchLaunchKpiCalculation',
  async (payload: any, { dispatch }) => {
    try {
      const response: InputAdditionKpiResponse = await fetchLaunchKpiCalculationData(payload);
      return response;
    } catch (error) {
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  },
);

const inputKpiSlice = createSlice({
  name: 'inputKpi',
  initialState,
  reducers: {
    resetInputKpiState: (state) => {
      const currentTab = state.current_tab;
      return {
        ...initialState,
        current_tab: currentTab,
      };
    },
    setCurrentTab: (state, action: PayloadAction<string>) => {
      state.current_tab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchKpiCalculation.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchKpiCalculation.fulfilled, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.current_price = action.payload.current_price;
      state.competition_price = action.payload.competition_price;
      state.distribution = action.payload.distribution;
      state.current_distribution = action.payload.current_distribution;
      state.competition_distribution = action.payload.competition_distribution;
      state.min_price = action.payload.min_price;
      state.max_price = action.payload.max_price;
      state.input_base_price = action.payload.input_base_price
    });
    builder.addCase(fetchKpiCalculation.rejected, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload || 'Failed to fetch KPI data';
    });
    builder.addCase(fetchLaunchKpiCalculation.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchLaunchKpiCalculation.fulfilled, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.distribution = action.payload.distribution;
      state.ko_segment_ppl = action.payload.ko_segment_ppl;
      state.ko_segment_distribution = action.payload.ko_segment_distribution;
      state.comp_segment_ppl = action.payload.comp_segment_ppl;
      state.comp_segment_distribution = action.payload.comp_segment_distribution;
      state.min_price = action.payload.min_price;
      state.max_price = action.payload.max_price;
    });
    builder.addCase(fetchLaunchKpiCalculation.rejected, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload || 'Failed to fetch Addition KPI data';
    });
  },
});

export const { resetInputKpiState, setCurrentTab } = inputKpiSlice.actions;

export const selectCurrentPrice = (state: RootState) => state.inputKpi.current_price;
export const selectCompetitionPrice = (state: RootState) => state.inputKpi.competition_price;
export const selectCurrentDistribution = (state: RootState) => state.inputKpi.current_distribution;
export const selectCompetitionDistribution = (state: RootState) => state.inputKpi.competition_distribution;
export const selectKoSegmentPpl = (state: RootState) => state.inputKpi.ko_segment_ppl;
export const selectCompSegmentPpl = (state: RootState) => state.inputKpi.comp_segment_ppl;
export const selectKoSegmentDistribution = (state: RootState) => state.inputKpi.ko_segment_distribution;
export const selectCompSegmentDistribution = (state: RootState) => state.inputKpi.comp_segment_distribution;
export const selectDistribution = (state: RootState) => state.inputKpi.distribution;
export const selectMinPrice = (state: RootState) => state.inputKpi.min_price;
export const selectMaxPrice = (state: RootState) => state.inputKpi.max_price;
export const selectLoading = (state: RootState) => state.inputKpi.loading;
export const selectError = (state: RootState) => state.inputKpi.error;

export default inputKpiSlice.reducer;
